/**
 * Admin Business/PUDO Management Page
 * Full CRUD: View, Add, Approve, Reject, Suspend, Delete businesses
 * + Send Invite Email feature
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Link2, 
  ArrowLeft, 
  Store,
  Package,
  Search,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Building,
  Phone,
  Mail,
  MapPin,
  Plus,
  Trash2,
  Ban,
  Play,
  X,
  Send,
  UserPlus,
  Users,
  Edit2,
  Navigation
} from 'lucide-react';

const STATUS_COLORS = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  approved: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
  rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
  suspended: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
};

const BUSINESS_TYPE_ICONS = {
  retail: '🏪',
  restaurant: '🍽️',
  services: '🔧',
  wholesale: '📦',
  manufacturer: '🏭',
  other: '🏢',
};

const AdminBusinessManagement = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addType, setAddType] = useState('seller'); // 'seller' or 'pudo'
  const [showAddSellerModal, setShowAddSellerModal] = useState(false);
  const [showAddPudoModal, setShowAddPudoModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteBusiness, setInviteBusiness] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Add business form
  const [newBusiness, setNewBusiness] = useState({
    owner_email: '',
    business_name: '',
    business_type: 'retail',
    trn: '',
    is_pudo: false,
    owner_name: '',
    owner_phone: '',
    send_credentials_email: true,
    existing_user_id: '',
    // PUDO Address fields
    pudo_address: '',
    pudo_city: '',
    pudo_parish: 'Kingston',
    pudo_hours: 'Mon-Sat 9:00 AM - 6:00 PM',
    pudo_latitude: '',
    pudo_longitude: ''
  });
  
  // Edit PUDO state
  const [editingPudo, setEditingPudo] = useState(null);
  const [showEditPudoModal, setShowEditPudoModal] = useState(false);
  
  // Existing users for linking
  const [existingUsers, setExistingUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [useExistingUser, setUseExistingUser] = useState(false);

  useEffect(() => {
    if (!isAdmin()) {
      toast.error('Access denied');
      navigate('/dashboard');
      return;
    }
    fetchBusinesses();
  }, [isAdmin, navigate]);

  const fetchBusinesses = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/businesses/');
      setBusinesses(response.data || []);
    } catch (error) {
      console.error('Error fetching businesses:', error);
      toast.error('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  const searchExistingUsers = async (query) => {
    if (!query || query.length < 2) {
      setExistingUsers([]);
      return;
    }
    try {
      const response = await api.get(`/admin/users/?search=${encodeURIComponent(query)}&limit=10`);
      // Show all users - admin can decide whether to update existing or create new
      const usersFound = response.data?.users || response.data || [];
      setExistingUsers(usersFound);
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  const selectExistingUser = (user) => {
    setNewBusiness({
      ...newBusiness,
      owner_email: user.email,
      owner_name: user.name || user.email.split('@')[0],
      owner_phone: user.phone || '',
      existing_user_id: user.id
    });
    setUserSearchQuery(user.email);
    setShowUserDropdown(false);
  };

  const handleApprove = async (businessId) => {
    setActionLoading(businessId);
    try {
      await api.post(`/admin/businesses/${businessId}/approve`);
      toast.success('Business approved successfully');
      fetchBusinesses();
      setShowDetailModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve business');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (businessId) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;
    
    setActionLoading(businessId);
    try {
      await api.post(`/admin/businesses/${businessId}/reject`, null, { params: { reason } });
      toast.success('Business rejected');
      fetchBusinesses();
      setShowDetailModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject business');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSuspend = async (businessId) => {
    if (!window.confirm('Suspend this business?')) return;
    
    setActionLoading(businessId);
    try {
      await api.post(`/admin/businesses/${businessId}/suspend`);
      toast.success('Business suspended');
      fetchBusinesses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend business');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivate = async (businessId) => {
    setActionLoading(businessId);
    try {
      await api.post(`/admin/businesses/${businessId}/reactivate`);
      toast.success('Business reactivated');
      fetchBusinesses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate business');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (businessId) => {
    if (!window.confirm('Permanently delete this business? This action cannot be undone.')) return;
    
    setActionLoading(businessId);
    try {
      await api.delete(`/admin/businesses/${businessId}`);
      toast.success('Business deleted');
      fetchBusinesses();
      setShowDetailModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete business');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddBusiness = async () => {
    if (!newBusiness.owner_email || !newBusiness.business_name) {
      toast.error('Owner email and business name are required');
      return;
    }
    
    // If linking to existing user, include user_id
    const payload = { ...newBusiness, is_pudo: addType === 'pudo' };
    if (useExistingUser && newBusiness.existing_user_id) {
      payload.link_existing_user = true;
      payload.user_id = newBusiness.existing_user_id;
    }
    
    setActionLoading('add');
    try {
      await api.post('/admin/businesses/create', newBusiness);
      toast.success('Business added successfully');
      setShowAddModal(false);
      setNewBusiness({ owner_email: '', business_name: '', business_type: 'retail', trn: '', is_pudo: false, pudo_address: '', pudo_city: '', pudo_parish: 'Kingston', pudo_hours: 'Mon-Sat 9:00 AM - 6:00 PM', pudo_latitude: '', pudo_longitude: '',
    owner_name: '',
    owner_phone: '',
    send_credentials_email: true });
      fetchBusinesses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add business');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendInviteEmail = async (business, includePassword = true) => {
    setActionLoading(`invite-${business.id}`);
    try {
      const response = await api.post(`/admin/invites/send-business-invite/${business.id}`, null, {
        params: { include_temp_password: includePassword }
      });
      
      if (response.data.success) {
        toast.success(`Invite email sent to ${response.data.email_sent_to}`);
        setShowInviteModal(false);
        setInviteBusiness(null);
      } else {
        toast.error(response.data.message || 'Failed to send invite');
      }
    } catch (error) {
      console.error('Error sending invite:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invite email');
    } finally {
      setActionLoading(null);
    }
  };

  const openInviteModal = (business) => {
    setInviteBusiness(business);
    setShowInviteModal(true);
  };
  
  // Open Edit PUDO Modal
  const openEditPudoModal = async (business) => {
    try {
      // Fetch PUDO location details
      const pudoRes = await api.get('/pudo/locations');
      const pudoLocation = pudoRes.data.locations?.find(p => p.business_id === business.id) || 
                          pudoRes.data.locations?.find(p => p.owner_id === business.owner_id);
      
      setEditingPudo({
        business_id: business.id,
        business_name: business.business_name,
        pudo_id: pudoLocation?.id || null,
        address: pudoLocation?.address || '',
        city: pudoLocation?.city || '',
        parish: pudoLocation?.parish || 'Kingston',
        hours: pudoLocation?.hours || 'Mon-Sat 9:00 AM - 6:00 PM',
        latitude: pudoLocation?.latitude || '',
        longitude: pudoLocation?.longitude || '',
        is_active: pudoLocation?.is_active ?? true
      });
      setShowEditPudoModal(true);
    } catch (error) {
      toast.error('Failed to load PUDO details');
    }
  };
  
  // Save PUDO edits
  const handleSavePudoEdit = async () => {
    if (!editingPudo.address || !editingPudo.city) {
      toast.error('Address and city are required');
      return;
    }
    
    setActionLoading('edit-pudo');
    try {
      await api.put('/business/admin/pudo/update-location', {
        business_id: editingPudo.business_id,
        pudo_id: editingPudo.pudo_id,
        address: editingPudo.address,
        city: editingPudo.city,
        parish: editingPudo.parish,
        hours: editingPudo.hours,
        latitude: editingPudo.latitude ? parseFloat(editingPudo.latitude) : null,
        longitude: editingPudo.longitude ? parseFloat(editingPudo.longitude) : null,
        is_active: editingPudo.is_active
      });
      
      toast.success('PUDO location updated successfully');
      setShowEditPudoModal(false);
      setEditingPudo(null);
      fetchBusinesses();
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Failed to update PUDO');
    } finally {
      setActionLoading(null);
    }
  };

  const viewBusinessDetails = (business) => {
    setSelectedBusiness(business);
    setShowDetailModal(true);
  };

  // Filter businesses
  const filteredBusinesses = businesses.filter(business => {
    const matchesSearch = 
      business.business_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      business.owner_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      business.trn?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || business.verification_status === statusFilter;
    
    const matchesType = typeFilter === 'all' || 
      (typeFilter === 'pudo' && business.is_pudo) ||
      (typeFilter === 'merchant' && !business.is_pudo);
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const stats = {
    total: businesses.length,
    pending: businesses.filter(b => b.verification_status === 'pending').length,
    approved: businesses.filter(b => b.verification_status === 'approved').length,
    suspended: businesses.filter(b => b.verification_status === 'suspended').length,
    pudo: businesses.filter(b => b.is_pudo).length,
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin')}
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-white hover:bg-slate-800"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3">
              <Link2 className="h-7 w-7 text-amber-500" />
              <span className="text-xl font-bold text-white tracking-tight">Liantiao</span>
              <span className="text-xs font-mono text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded border border-emerald-500/20">
                BUSINESS/PUDO
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={fetchBusinesses}
              variant="outline"
              size="sm"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
              disabled={loading}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Building className="h-5 w-5 text-slate-400" />
              <span className="text-slate-400 text-sm">Total</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
          </div>
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="h-5 w-5 text-yellow-400" />
              <span className="text-slate-400 text-sm">Pending</span>
            </div>
            <p className="text-2xl font-bold text-yellow-400">{stats.pending}</p>
          </div>
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="h-5 w-5 text-teal-400" />
              <span className="text-slate-400 text-sm">Approved</span>
            </div>
            <p className="text-2xl font-bold text-teal-400">{stats.approved}</p>
          </div>
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Ban className="h-5 w-5 text-orange-400" />
              <span className="text-slate-400 text-sm">Suspended</span>
            </div>
            <p className="text-2xl font-bold text-orange-400">{stats.suspended}</p>
          </div>
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Package className="h-5 w-5 text-purple-400" />
              <span className="text-slate-400 text-sm">PUDO Points</span>
            </div>
            <p className="text-2xl font-bold text-purple-400">{stats.pudo}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, owner, or TRN..."
              className="bg-slate-900 border-slate-700 pl-10 text-white"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {['all', 'pending', 'approved', 'suspended', 'rejected'].map((status) => (
              <Button
                key={status}
                onClick={() => setStatusFilter(status)}
                variant={statusFilter === status ? 'default' : 'outline'}
                size="sm"
                className={statusFilter === status 
                  ? 'bg-amber-500 text-slate-950' 
                  : 'border-slate-700 text-slate-300'
                }
                data-testid={`filter-${status}`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </Button>
            ))}
            <div className="w-px h-8 bg-slate-700 mx-1" />
            <Button
              onClick={() => setTypeFilter(typeFilter === 'pudo' ? 'all' : 'pudo')}
              variant={typeFilter === 'pudo' ? 'default' : 'outline'}
              size="sm"
              className={typeFilter === 'pudo' 
                ? 'bg-purple-500 text-white' 
                : 'border-slate-700 text-slate-300'
              }
              data-testid="filter-pudo"
            >
              <Package className="h-4 w-4 mr-1" />
              PUDO Only
            </Button>
          </div>
        </div>

        {/* Businesses Table */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="businesses-table">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-900/50">
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide p-4">Business</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide p-4">Type</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide p-4">Owner</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide p-4">Status</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide p-4">Applied</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center">
                      <RefreshCw className="h-6 w-6 text-amber-500 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : filteredBusinesses.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-slate-400">
                      No businesses found
                    </td>
                  </tr>
                ) : (
                  filteredBusinesses.map((business) => (
                    <tr 
                      key={business.id}
                      className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors"
                      data-testid={`business-row-${business.id}`}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
                            {business.is_pudo ? (
                              <Package className="w-5 h-5 text-purple-400" />
                            ) : (
                              <Store className="w-5 h-5 text-emerald-400" />
                            )}
                          </div>
                          <div>
                            <p className="text-white font-medium">{business.business_name}</p>
                            <p className="text-xs text-slate-500 font-mono">{business.id?.slice(0, 8)}...</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">
                            {BUSINESS_TYPE_ICONS[business.business_type] || '🏢'}
                          </span>
                          <div>
                            <p className="text-white capitalize text-sm">
                              {business.business_type || 'Business'}
                            </p>
                            {business.is_pudo && (
                              <span className="text-xs text-purple-400 flex items-center gap-1">
                                <Package className="h-3 w-3" /> PUDO
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <p className="text-slate-300 text-sm">{business.owner_name || '-'}</p>
                        <p className="text-slate-500 text-xs">{business.owner_email || '-'}</p>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-mono border ${STATUS_COLORS[business.verification_status] || STATUS_COLORS.pending}`}>
                          {business.verification_status || 'pending'}
                        </span>
                      </td>
                      <td className="p-4 text-slate-400 text-sm">
                        {business.created_at ? new Date(business.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          <Button onClick={() => viewBusinessDetails(business)} variant="ghost" size="sm" className="text-slate-400 hover:text-white" title="View Details" data-testid={`view-${business.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {business.is_pudo && (
                            <Button onClick={() => openEditPudoModal(business)} variant="ghost" size="sm" className="text-amber-400 hover:bg-amber-500/10" title="Edit PUDO Location">
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          )}
                          <Button onClick={() => openInviteModal(business)} variant="ghost" size="sm" className="text-blue-400 hover:bg-blue-500/10" title="Send Invite Email" data-testid={`invite-${business.id}`}>
                            <Mail className="h-4 w-4" />
                          </Button>
                          {business.verification_status === 'pending' && (
                            <>
                              <Button onClick={() => handleApprove(business.id)} variant="ghost" size="sm" className="text-teal-400 hover:bg-teal-500/10" title="Approve" disabled={actionLoading === business.id} data-testid={`approve-${business.id}`}>
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button onClick={() => handleReject(business.id)} variant="ghost" size="sm" className="text-red-400 hover:bg-red-500/10" title="Reject" disabled={actionLoading === business.id} data-testid={`reject-${business.id}`}>
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {business.verification_status === 'approved' && (
                            <Button onClick={() => handleSuspend(business.id)} variant="ghost" size="sm" className="text-orange-400 hover:bg-orange-500/10" title="Suspend" disabled={actionLoading === business.id} data-testid={`suspend-${business.id}`}>
                              <Ban className="h-4 w-4" />
                            </Button>
                          )}
                          {business.verification_status === 'suspended' && (
                            <Button onClick={() => handleReactivate(business.id)} variant="ghost" size="sm" className="text-teal-400 hover:bg-teal-500/10" title="Reactivate" disabled={actionLoading === business.id} data-testid={`reactivate-${business.id}`}>
                              <Play className="h-4 w-4" />
                            </Button>
                          )}
                          <Button onClick={() => handleDelete(business.id)} variant="ghost" size="sm" className="text-red-400 hover:bg-red-500/10" title="Delete" disabled={actionLoading === business.id} data-testid={`delete-${business.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Add Business Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-slate-900 border border-slate-800 rounded-lg w-full max-w-lg p-6 my-8 max-h-[90vh] overflow-y-auto" data-testid="add-business-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                {addType === 'pudo' ? <MapPin className="h-5 w-5 text-amber-500" /> : <Store className="h-5 w-5 text-emerald-500" />}
                {addType === 'pudo' ? 'Add New PUDO Point' : 'Add New Seller'}
              </h2>
              <Button onClick={() => setShowAddModal(false)} variant="ghost" size="sm" className="text-slate-400">
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              {/* Toggle: New User vs Existing User */}
              <div className="flex gap-2 p-1 bg-slate-800 rounded-lg">
                <button
                  type="button"
                  onClick={() => setUseExistingUser(false)}
                  className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-all ${!useExistingUser ? 'bg-teal-500 text-white' : 'text-slate-400 hover:text-white'}`}
                >
                  <Plus className="h-4 w-4 inline mr-1" />New User
                </button>
                <button
                  type="button"
                  onClick={() => setUseExistingUser(true)}
                  className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-all ${useExistingUser ? 'bg-teal-500 text-white' : 'text-slate-400 hover:text-white'}`}
                >
                  <Users className="h-4 w-4 inline mr-1" />Existing User
                </button>
              </div>

              {/* Existing User Search */}
              {useExistingUser && (
                <div className="relative">
                  <Label className="text-slate-300">Search Existing User *</Label>
                  <Input
                    value={userSearchQuery}
                    onChange={(e) => {
                      setUserSearchQuery(e.target.value);
                      searchExistingUsers(e.target.value);
                      setShowUserDropdown(true);
                    }}
                    onFocus={() => setShowUserDropdown(true)}
                    placeholder="Search by name or email..."
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                  {showUserDropdown && existingUsers.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {existingUsers.map((user) => (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => selectExistingUser(user)}
                          className="w-full px-3 py-2 text-left hover:bg-slate-700 flex items-center gap-2"
                        >
                          <div className="w-8 h-8 rounded-full bg-teal-500/20 flex items-center justify-center text-teal-400 text-sm font-medium">
                            {(user.name || user.email)[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="text-white text-sm">{user.name || 'No name'}</p>
                            <p className="text-slate-400 text-xs">{user.email}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {newBusiness.existing_user_id && (
                    <p className="text-xs text-teal-400 mt-1">✓ User selected: {newBusiness.owner_name}</p>
                  )}
                </div>
              )}

              {/* Owner Name - show for new user or auto-filled for existing */}
              <div>
                <Label className="text-slate-300">Owner Name *</Label>
                <Input
                  value={newBusiness.owner_name}
                  onChange={(e) => setNewBusiness({ ...newBusiness, owner_name: e.target.value })}
                  placeholder="John Doe"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="input-owner-name"
                  disabled={useExistingUser && newBusiness.existing_user_id}
                />
              </div>

              {/* Owner Email - show for new user or auto-filled for existing */}
              <div>
                <Label className="text-slate-300">Owner Email *</Label>
                <Input
                  value={newBusiness.owner_email}
                  onChange={(e) => setNewBusiness({ ...newBusiness, owner_email: e.target.value })}
                  placeholder="owner@example.com"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="input-owner-email"
                  disabled={useExistingUser && newBusiness.existing_user_id}
                />
              </div>
              <div>
                <Label className="text-slate-300">Business Name *</Label>
                <Input
                  value={newBusiness.business_name}
                  onChange={(e) => setNewBusiness({ ...newBusiness, business_name: e.target.value })}
                  placeholder="My Business"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="input-business-name"
                />
              </div>
              <div>
                <Label className="text-slate-300">Business Type</Label>
                <select
                  value={newBusiness.business_type}
                  onChange={(e) => setNewBusiness({ ...newBusiness, business_type: e.target.value })}
                  className="w-full bg-slate-800 border border-slate-700 text-white rounded-md p-2"
                  data-testid="select-business-type"
                >
                  <option value="retail">🏪 Retail</option>
                  <option value="restaurant">🍽️ Restaurant</option>
                  <option value="services">🔧 Services</option>
                  <option value="wholesale">📦 Wholesale</option>
                  <option value="manufacturer">🏭 Manufacturer</option>
                  <option value="other">🏢 Other</option>
                </select>
              </div>
              <div>
                <Label className="text-slate-300">TRN (Tax Registration Number)</Label>
                <Input
                  value={newBusiness.trn}
                  onChange={(e) => setNewBusiness({ ...newBusiness, trn: e.target.value })}
                  placeholder="123-456-789"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="input-trn"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_pudo"
                  checked={newBusiness.is_pudo}
                  onChange={(e) => setNewBusiness({ ...newBusiness, is_pudo: e.target.checked })}
                  className="w-4 h-4 rounded bg-slate-800 border-slate-700"
                  data-testid="checkbox-pudo"
                />
                <Label htmlFor="is_pudo" className="text-slate-300">Register as PUDO Point</Label>
              </div>
              
              {/* PUDO Address Fields - Show when is_pudo is checked */}
              {addType === 'pudo' && (
                <div className="space-y-3 p-4 bg-teal-500/10 border border-teal-500/30 rounded-lg">
                  <p className="text-teal-400 text-sm font-medium flex items-center gap-2">
                    <MapPin className="h-4 w-4" /> PUDO Location Details
                  </p>
                  <div>
                    <Label className="text-slate-300">Street Address *</Label>
                    <Input
                      value={newBusiness.pudo_address}
                      onChange={(e) => setNewBusiness({ ...newBusiness, pudo_address: e.target.value })}
                      placeholder="123 Main Street"
                      className="bg-slate-800 border-slate-700 text-white"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-300">City *</Label>
                      <Input
                        value={newBusiness.pudo_city}
                        onChange={(e) => setNewBusiness({ ...newBusiness, pudo_city: e.target.value })}
                        placeholder="Kingston"
                        className="bg-slate-800 border-slate-700 text-white"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Parish *</Label>
                      <select
                        value={newBusiness.pudo_parish}
                        onChange={(e) => setNewBusiness({ ...newBusiness, pudo_parish: e.target.value })}
                        className="w-full h-10 rounded-md bg-slate-800 border border-slate-700 text-white px-3"
                      >
                        <option value="Kingston">Kingston</option>
                        <option value="St. Andrew">St. Andrew</option>
                        <option value="St. Catherine">St. Catherine</option>
                        <option value="Clarendon">Clarendon</option>
                        <option value="Manchester">Manchester</option>
                        <option value="St. Elizabeth">St. Elizabeth</option>
                        <option value="Westmoreland">Westmoreland</option>
                        <option value="Hanover">Hanover</option>
                        <option value="St. James">St. James</option>
                        <option value="Trelawny">Trelawny</option>
                        <option value="St. Ann">St. Ann</option>
                        <option value="St. Mary">St. Mary</option>
                        <option value="Portland">Portland</option>
                        <option value="St. Thomas">St. Thomas</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-300">Operating Hours</Label>
                    <Input
                      value={newBusiness.pudo_hours}
                      onChange={(e) => setNewBusiness({ ...newBusiness, pudo_hours: e.target.value })}
                      placeholder="Mon-Sat 9:00 AM - 6:00 PM"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-300">Latitude (Optional)</Label>
                      <Input
                        type="number"
                        step="any"
                        value={newBusiness.pudo_latitude}
                        onChange={(e) => setNewBusiness({ ...newBusiness, pudo_latitude: e.target.value })}
                        placeholder="18.0179"
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Longitude (Optional)</Label>
                      <Input
                        type="number"
                        step="any"
                        value={newBusiness.pudo_longitude}
                        onChange={(e) => setNewBusiness({ ...newBusiness, pudo_longitude: e.target.value })}
                        placeholder="-76.8099"
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <Label className="text-slate-300">Owner Telephone (Optional)</Label>
                <Input
                  value={newBusiness.owner_phone}
                  onChange={(e) => setNewBusiness({ ...newBusiness, owner_phone: e.target.value })}
                  placeholder="+1 876 123 4567"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="input-owner-phone"
                />
              </div>
              <div className="flex items-center gap-3 pt-2 border-t border-slate-700">
                <input
                  type="checkbox"
                  id="send-credentials-biz"
                  checked={newBusiness.send_credentials_email}
                  onChange={(e) => setNewBusiness({ ...newBusiness, send_credentials_email: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-teal-500 focus:ring-teal-500"
                  data-testid="send-credentials-checkbox"
                />
                <label htmlFor="send-credentials-biz" className="text-slate-300 text-sm">
                  Send login credentials via email
                </label>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button onClick={() => setShowAddModal(false)} variant="outline" className="flex-1 border-slate-700 text-slate-300">
                Cancel
              </Button>
              <Button onClick={handleAddBusiness} className={`flex-1 text-white ${addType === "pudo" ? "bg-amber-500 hover:bg-amber-400" : "bg-emerald-500 hover:bg-emerald-400"}`} disabled={actionLoading === "add"} data-testid="submit-add-business">
                {actionLoading === 'add' ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Add Business
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Send Invite Email Modal */}
      {showInviteModal && inviteBusiness && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="invite-modal">
          <div className="bg-slate-900 border border-slate-800 rounded-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Mail className="h-5 w-5 text-amber-500" />
                Send Invite Email
              </h2>
              <Button onClick={() => { setShowInviteModal(false); setInviteBusiness(null); }} variant="ghost" size="sm" className="text-slate-400">
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
              <p className="text-slate-400 text-sm mb-1">Sending invite to:</p>
              <p className="text-white font-medium">{inviteBusiness.business_name}</p>
              <p className="text-slate-300 text-sm">{inviteBusiness.owner_name || 'Business Owner'}</p>
              <p className="text-slate-400 text-sm">{inviteBusiness.owner_email}</p>
            </div>
            
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
              <p className="text-amber-400 text-sm">
                <strong>Note:</strong> This will generate a temporary password and send it to the business owner via email. They will be prompted to change their password on first login.
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button onClick={() => { setShowInviteModal(false); setInviteBusiness(null); }} variant="outline" className="flex-1 border-slate-700 text-slate-300">
                Cancel
              </Button>
              <Button 
                onClick={() => handleSendInviteEmail(inviteBusiness, true)} 
                className="flex-1 bg-amber-500 text-slate-950 hover:bg-amber-400" 
                disabled={actionLoading === `invite-${inviteBusiness.id}`}
                data-testid="send-invite-btn"
              >
                {actionLoading === `invite-${inviteBusiness.id}` ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Send Invite
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedBusiness && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="business-detail-modal">
            <div className="p-6 border-b border-slate-800 flex items-center justify-between sticky top-0 bg-slate-900">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Building className="h-5 w-5 text-amber-500" />
                Business Details
              </h2>
              <Button onClick={() => setShowDetailModal(false)} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Business Name</p>
                  <p className="text-white font-medium">{selectedBusiness.business_name || 'Unknown'}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Status</p>
                  <span className={`px-2 py-1 rounded-full text-xs font-mono border ${STATUS_COLORS[selectedBusiness.verification_status] || STATUS_COLORS.pending}`}>
                    {selectedBusiness.verification_status || 'pending'}
                  </span>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Owner</p>
                  <p className="text-white">{selectedBusiness.owner_name || 'N/A'}</p>
                  <p className="text-slate-400 text-xs">{selectedBusiness.owner_email || ''}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Type</p>
                  <p className="text-white flex items-center gap-2">
                    {BUSINESS_TYPE_ICONS[selectedBusiness.business_type] || '🏢'}
                    <span className="capitalize">{selectedBusiness.business_type || 'Other'}</span>
                    {selectedBusiness.is_pudo && (
                      <span className="text-xs text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded border border-purple-500/20">PUDO</span>
                    )}
                  </p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">TRN</p>
                  <p className="text-white font-mono">{selectedBusiness.trn || 'N/A'}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Created</p>
                  <p className="text-white">{selectedBusiness.created_at ? new Date(selectedBusiness.created_at).toLocaleDateString() : 'N/A'}</p>
                </div>
              </div>

              {selectedBusiness.addresses && selectedBusiness.addresses.length > 0 && (
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-2">Address</p>
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-white">{selectedBusiness.addresses[0].street || ''}</p>
                      <p className="text-slate-400 text-sm">{selectedBusiness.addresses[0].city}, {selectedBusiness.addresses[0].parish}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-800 flex gap-3 flex-wrap">
              <Button 
                onClick={() => openInviteModal(selectedBusiness)} 
                className="bg-amber-500 text-slate-950 hover:bg-amber-400"
                data-testid="detail-send-invite-btn"
              >
                <Mail className="h-4 w-4 mr-2" /> Send Invite Email
              </Button>
              {selectedBusiness.verification_status === 'pending' && (
                <>
                  <Button onClick={() => handleApprove(selectedBusiness.id)} className="bg-teal-500 text-white" disabled={actionLoading === selectedBusiness.id}>
                    <CheckCircle className="h-4 w-4 mr-2" /> Approve
                  </Button>
                  <Button onClick={() => handleReject(selectedBusiness.id)} variant="outline" className="border-red-500/30 text-red-400" disabled={actionLoading === selectedBusiness.id}>
                    <XCircle className="h-4 w-4 mr-2" /> Reject
                  </Button>
                </>
              )}
              {selectedBusiness.verification_status === 'approved' && (
                <Button onClick={() => handleSuspend(selectedBusiness.id)} variant="outline" className="border-orange-500/30 text-orange-400" disabled={actionLoading === selectedBusiness.id}>
                  <Ban className="h-4 w-4 mr-2" /> Suspend
                </Button>
              )}
              {selectedBusiness.verification_status === 'suspended' && (
                <Button onClick={() => handleReactivate(selectedBusiness.id)} className="bg-teal-500 text-white" disabled={actionLoading === selectedBusiness.id}>
                  <Play className="h-4 w-4 mr-2" /> Reactivate
                </Button>
              )}
              <Button onClick={() => handleDelete(selectedBusiness.id)} variant="outline" className="border-red-500/30 text-red-400 ml-auto" disabled={actionLoading === selectedBusiness.id}>
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Edit PUDO Location Modal */}
      {showEditPudoModal && editingPudo && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="edit-pudo-modal">
          <div className="bg-slate-900 border border-slate-800 rounded-lg w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Navigation className="h-5 w-5 text-amber-500" />
                Edit PUDO Location
              </h2>
              <Button onClick={() => { setShowEditPudoModal(false); setEditingPudo(null); }} variant="ghost" size="sm" className="text-slate-400">
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-3 mb-4">
              <p className="text-white font-medium">{editingPudo.business_name}</p>
              <p className="text-slate-400 text-sm">PUDO ID: {editingPudo.pudo_id || 'Not created yet'}</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Street Address *</Label>
                <Input
                  value={editingPudo.address}
                  onChange={(e) => setEditingPudo({ ...editingPudo, address: e.target.value })}
                  placeholder="123 Main Street"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300">City *</Label>
                  <Input
                    value={editingPudo.city}
                    onChange={(e) => setEditingPudo({ ...editingPudo, city: e.target.value })}
                    placeholder="Kingston"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Parish</Label>
                  <select
                    value={editingPudo.parish}
                    onChange={(e) => setEditingPudo({ ...editingPudo, parish: e.target.value })}
                    className="w-full h-10 rounded-md bg-slate-800 border border-slate-700 text-white px-3"
                  >
                    <option value="Kingston">Kingston</option>
                    <option value="St. Andrew">St. Andrew</option>
                    <option value="St. Catherine">St. Catherine</option>
                    <option value="Clarendon">Clarendon</option>
                    <option value="Manchester">Manchester</option>
                    <option value="St. Elizabeth">St. Elizabeth</option>
                    <option value="Westmoreland">Westmoreland</option>
                    <option value="Hanover">Hanover</option>
                    <option value="St. James">St. James</option>
                    <option value="Trelawny">Trelawny</option>
                    <option value="St. Ann">St. Ann</option>
                    <option value="St. Mary">St. Mary</option>
                    <option value="Portland">Portland</option>
                    <option value="St. Thomas">St. Thomas</option>
                  </select>
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300">Operating Hours</Label>
                <Input
                  value={editingPudo.hours}
                  onChange={(e) => setEditingPudo({ ...editingPudo, hours: e.target.value })}
                  placeholder="Mon-Sat 9:00 AM - 6:00 PM"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-blue-400 text-sm font-medium mb-2 flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> GPS Coordinates (for tracking)
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-300 text-xs">Latitude</Label>
                    <Input
                      type="number"
                      step="any"
                      value={editingPudo.latitude}
                      onChange={(e) => setEditingPudo({ ...editingPudo, latitude: e.target.value })}
                      placeholder="18.0179"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-xs">Longitude</Label>
                    <Input
                      type="number"
                      step="any"
                      value={editingPudo.longitude}
                      onChange={(e) => setEditingPudo({ ...editingPudo, longitude: e.target.value })}
                      placeholder="-76.8099"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="pudo-active"
                  checked={editingPudo.is_active}
                  onChange={(e) => setEditingPudo({ ...editingPudo, is_active: e.target.checked })}
                  className="w-4 h-4 rounded bg-slate-800 border-slate-700"
                />
                <Label htmlFor="pudo-active" className="text-slate-300">PUDO Location is Active</Label>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button onClick={() => { setShowEditPudoModal(false); setEditingPudo(null); }} variant="outline" className="flex-1 border-slate-700 text-slate-300">
                Cancel
              </Button>
              <Button onClick={handleSavePudoEdit} className="flex-1 bg-amber-500 text-white hover:bg-amber-400" disabled={actionLoading === 'edit-pudo'}>
                {actionLoading === 'edit-pudo' ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBusinessManagement;
